import { composeSourceContentTitle } from '../source-content.js'

/**
 * One reversible projection policy shared by the bootstrap, runtime registry
 * synchronization, and reconciliation paths. Keep catalog-specific semantics
 * here so those callers cannot drift independently.
 */
export const LEARNING_ITEM_TRACK_BY_KIND = {
  problem: 'coding',
  coding_exercise: 'coding',
  system_design: 'system-design',
  task: 'task',
  resource: 'resource',
  source_content: 'source-content',
  behavioral: 'behavioral',
} as const

export const LEARNING_ITEM_CATALOG_KIND_ORDER = [
  'problem',
  'coding_exercise',
  'system_design',
  'task',
  'resource',
  'source_content',
  'behavioral',
] as const satisfies readonly (keyof typeof LEARNING_ITEM_TRACK_BY_KIND)[]

/**
 * How a card of each catalog kind gets finished.
 *
 * A total map rather than a list-membership test, so a kind added to
 * `LEARNING_ITEM_TRACK_BY_KIND` cannot silently inherit whichever branch the
 * test happened to fall through to. Coding-shaped kinds hand work in for
 * review; the rest are ticked off by the coach, because there is nothing for
 * the client to submit.
 *
 * Shared rather than duplicated: the curriculum board decides this when the
 * coach adds one item, and `cascadeAssign` decides it server-side for every
 * member of a container (LG-907). Two copies of the rule would let a
 * cascaded card and a hand-added card of the same kind finish differently.
 */
export const LEARNING_ITEM_COMPLETION_MODE = {
  problem: 'submission_review',
  coding_exercise: 'submission_review',
  system_design: 'submission_review',
  behavioral: 'submission_review',
  task: 'manual',
  resource: 'manual',
  source_content: 'manual',
} as const satisfies Record<
  keyof typeof LEARNING_ITEM_TRACK_BY_KIND,
  'manual' | 'submission_review'
>

export const PROBLEM_SUBTOPIC_WEIGHT = {
  primary: 1,
  secondary: 0.5,
} as const

export type CatalogLearningItemKind = keyof typeof LEARNING_ITEM_TRACK_BY_KIND
export type ProblemSubtopicWeight = keyof typeof PROBLEM_SUBTOPIC_WEIGHT

export interface CatalogSourceProjection {
  catalogId: string
  kind: CatalogLearningItemKind
  track: (typeof LEARNING_ITEM_TRACK_BY_KIND)[CatalogLearningItemKind]
  title: string
  estimatedMinutes: number | null
  problemId: string | null
  codingExerciseId: string | null
  systemDesignProblemId: string | null
  taskId: string | null
  resourceId: string | null
  sourceContentId: string | null
  behavioralQuestionId: string | null
  deletedAt: Date | null
}

interface BaseCatalogSource {
  id: string
  title: string
  deletedAt: Date | null
}

type CatalogSourceInputByKind = {
  problem: BaseCatalogSource
  coding_exercise: BaseCatalogSource & { estimatedMinutes: number | null }
  system_design: BaseCatalogSource & { estimatedMinutes: number | null }
  task: BaseCatalogSource & { estimatedMinutes: number | null }
  resource: BaseCatalogSource & { durationMinutes: number | null }
  source_content: BaseCatalogSource & { estimatedMinutes: number | null }
  behavioral: BaseCatalogSource & { estimatedMinutes: number }
}

type CatalogIdentityColumns = Pick<
  CatalogSourceProjection,
  | 'problemId'
  | 'codingExerciseId'
  | 'systemDesignProblemId'
  | 'taskId'
  | 'resourceId'
  | 'sourceContentId'
  | 'behavioralQuestionId'
>

const EMPTY_CATALOG_IDENTITIES: CatalogIdentityColumns = {
  problemId: null,
  codingExerciseId: null,
  systemDesignProblemId: null,
  taskId: null,
  resourceId: null,
  sourceContentId: null,
  behavioralQuestionId: null,
} as const

export function projectCatalogSource<TKind extends CatalogLearningItemKind>(
  kind: TKind,
  source: CatalogSourceInputByKind[TKind],
): CatalogSourceProjection {
  const identity: CatalogIdentityColumns = { ...EMPTY_CATALOG_IDENTITIES }
  let estimatedMinutes: number | null

  switch (kind) {
    case 'problem':
      identity.problemId = source.id
      estimatedMinutes = null
      break
    case 'coding_exercise':
      identity.codingExerciseId = source.id
      estimatedMinutes = (source as CatalogSourceInputByKind['coding_exercise'])
        .estimatedMinutes
      break
    case 'system_design':
      identity.systemDesignProblemId = source.id
      estimatedMinutes = (source as CatalogSourceInputByKind['system_design'])
        .estimatedMinutes
      break
    case 'task':
      identity.taskId = source.id
      estimatedMinutes = (source as CatalogSourceInputByKind['task'])
        .estimatedMinutes
      break
    case 'resource':
      identity.resourceId = source.id
      estimatedMinutes = (source as CatalogSourceInputByKind['resource'])
        .durationMinutes
      break
    case 'source_content':
      identity.sourceContentId = source.id
      estimatedMinutes = (source as CatalogSourceInputByKind['source_content'])
        .estimatedMinutes
      break
    case 'behavioral':
      identity.behavioralQuestionId = source.id
      estimatedMinutes = (source as CatalogSourceInputByKind['behavioral'])
        .estimatedMinutes
      break
  }

  return {
    catalogId: source.id,
    kind,
    track: LEARNING_ITEM_TRACK_BY_KIND[kind],
    title: source.title,
    estimatedMinutes,
    ...identity,
    deletedAt: source.deletedAt,
  }
}

/**
 * The ONLY supported way to project a `source_content` row into the shared
 * catalog projection. `LearningItem.title` for a source container composes
 * as `actionVerb + title` ("Read Ch 19"), and `matchesCatalogSourceProjection`
 * compares projected titles to detect drift — if any writer of a
 * `source_content` `LearningItem` composed the title differently, the
 * reconcile job would see every row as perpetually stale and rewrite it on
 * every pass, forever. Every call site (runtime registry sync, backfill
 * snapshot collector, backfill per-row re-read) MUST route through this
 * function instead of calling `projectCatalogSource('source_content', ...)`
 * directly.
 */
export function projectSourceContentCatalogSource(source: {
  id: string
  title: string
  actionVerb: string | null
  estimatedMinutes: number | null
  deletedAt: Date | null
}): CatalogSourceProjection {
  return projectCatalogSource('source_content', {
    ...source,
    title: composeSourceContentTitle(source.title, source.actionVerb),
  })
}

export function projectProblemSubtopicWeight(
  weight: ProblemSubtopicWeight,
): number {
  return PROBLEM_SUBTOPIC_WEIGHT[weight]
}

export function matchesCatalogSourceProjection(
  row: Omit<CatalogSourceProjection, 'deletedAt'>,
  projection: CatalogSourceProjection,
): boolean {
  return (
    row.kind === projection.kind &&
    row.track === projection.track &&
    row.title === projection.title &&
    row.estimatedMinutes === projection.estimatedMinutes &&
    row.problemId === projection.problemId &&
    row.codingExerciseId === projection.codingExerciseId &&
    row.systemDesignProblemId === projection.systemDesignProblemId &&
    row.taskId === projection.taskId &&
    row.resourceId === projection.resourceId &&
    row.sourceContentId === projection.sourceContentId &&
    row.behavioralQuestionId === projection.behavioralQuestionId
  )
}
